import {z} from 'zod';
import JoditEditor from 'jodit-react';
import React, {FC, useRef} from 'react';
import {observer} from 'mobx-react-lite';
import {zodResolver} from '@hookform/resolvers/zod';
import {VerdocsButton} from '@verdocs/web-sdk-react';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {IOrganization, getOrganization, updateOrganization, VerdocsEndpoint} from '@verdocs/js-sdk';
import {FormColumn, FormColumns, PageTitle} from '../../Components';
import {track} from '../../Managers/Analytics';
import {AppState} from '../../AppState';

const DEFAULT_DISCLOSURES = `<ul>
  <li>
    Agree to use electronic records and signatures, and confirm you have read the
    <a href="https://verdocs.com/en/electronic-record-signature-disclosure/" target="_blank">
      Electronic Record and Signatures Disclosure</a>.</li>
  <li>
    Agree to Verdocs'
    <a href="https://verdocs.com/en/eula" target="_blank">
      End User License Agreement</a>
    and confirm you have read Verdocs'
    <a href="https://verdocs.com/en/privacy-policy/" target="_blank">
      Privacy Policy</a>.
  </li>
</ul>`;

const schema = z.object({
  disclaimer: z.string().trim(),
});

type SchemaType = z.infer<typeof schema>;

const getEditableFields = (organization?: IOrganization) => ({
  disclaimer: organization?.disclaimer || DEFAULT_DISCLOSURES,
});

export const Disclaimer: FC = observer(() => {
  const organizationId = AppState.currentProfile?.organization_id || '';
  const editor = useRef(null);

  const form = useForm<SchemaType>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: async () => getOrganization(VerdocsEndpoint.getDefault(), organizationId).then(getEditableFields),
  });

  const onSubmit = async (data: any) => {
    await updateOrganization(VerdocsEndpoint.getDefault(), organizationId, data)
      .then((r) => {
        console.log('Updated', r);
        track('settings_updated', {section: 'disclosures'});
      })
      .catch((e) => console.log('Unknown error', e));
  };

  const dirty = form.formState.isDirty;

  return (
    <div className="max-w-[640px]">
      <PageTitle>Custom Disclosures</PageTitle>

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <FormColumns>
            <FormColumn className="flex-1">
              <Controller
                render={({field: {onChange, onBlur, value}}) => (
                  <JoditEditor
                    ref={editor}
                    value={value}
                    // all options from https://xdsoft.net/jodit/docs/,
                    config={{readonly: false, placeholder: '', hidePoweredByJodit: true}}
                    onBlur={(text) => {
                      onChange(text);
                      onBlur();
                    }}
                    onChange={() => {}}
                  />
                )}
                name="disclaimer"
              />
            </FormColumn>
          </FormColumns>

          <div className="flex flex-row gap-4">
            <VerdocsButton type="submit" label="Save" size="normal" disabled={form.formState.isSubmitting || !dirty} />
            <VerdocsButton
              label="Reset to Default"
              size="normal"
              disabled={form.getValues('disclaimer') === DEFAULT_DISCLOSURES}
              onClick={() => {
                form.setValue('disclaimer', DEFAULT_DISCLOSURES, {shouldDirty: true, shouldTouch: true});
              }}
            />
          </div>
        </form>
      </FormProvider>
    </div>
  );
});
